import { Icon, ResponsiveImage } from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { VLayout } from '@sleepstation/components-layouts'
import { SIZE } from '@sleepstation/const'
import {
  borderTopRadius,
  color,
  mq,
  right,
  stylesIfPropTrue,
  top,
} from '@sleepstation/style-api-helpers'
import {
  classStartsWith,
  mqPointerOnly,
  outset,
} from '@sleepstation/style-mixins'
import { reversedTheme } from '@sleepstation/themes-public-site'
import { rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { isNotNil } from 'ramda-adjunct'
import React from 'react'
import styled, { ThemeProvider, css } from 'styled-components'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const ILLUSTRATION_ALT = `Illustration`

const ILLUSTRATION_ASPECT_RATIO = 586 / 310

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getLinkStyles = () => css`
  display: block;
  height: 100%;
  text-decoration-line: none;

  ${mq(`mediumOnly`)} {
    max-width: ${rem(452)};
  }

  ${mq(`largeUp`)} {
    max-width: ${rem(592)};
  }
`

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Link = styled(LinkBase)`
  ${getLinkStyles()}
`

const FauxLink = styled.div`
  ${getLinkStyles()}
`

const Wrapper = ({ href, dataCy, children }) =>
  isNotNil(href) ? (
    <Link data-cy={dataCy} href={href}>
      {children}
    </Link>
  ) : (
    <FauxLink>{children}</FauxLink>
  )

const Layout = styled(VLayout)`
  ${outset()}
  ${borderTopRadius(0.5)}
  overflow: hidden;
  height: 100%;
  position: relative;

  ${stylesIfPropTrue(
    `isActive`,
    css`
      ${mqPointerOnly()} {
        &:hover {
          ${classStartsWith(`ResponsiveImage__Layout`)} {
            will-change: transform;
            transform: scale3d(1.025, 1.025, 1);
          }
        }
      }
    `
  )}
`

Wrapper.propTypes = {
  href: PropTypes.string,
  dataCy: PropTypes.string,
  children: PropTypes.node.isRequired,
}

Wrapper.defaultProps = {
  dataCy: null,
  href: null,
}

const Body = styled.div``

const ImageWrapper = styled.div`
  ${borderTopRadius(0.5)}
  overflow: hidden;
  mask-image: radial-gradient(white, black);

  /* stylelint-disable-next-line */
  ${classStartsWith(`ResponsiveImage__Layout`)} {
    display: block;
    width: 100%;
    image-rendering: smooth;
    transition: transform 0.25s ease-in-out;
  }
`

const IconWrapper = styled.div`
  position: absolute;
  ${top(0.5)}
  ${right(0.5)}
  ${color(`body`)}
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Card = ({ image, url, iconName, children, dataCy }) => {
  const isActive = isNotNil(url)
  return (
    <Wrapper href={url} dataCy={dataCy}>
      <Layout isActive={isActive}>
        <ImageWrapper>
          {image && (
            <ResponsiveImage
              className={`illustration`}
              aspectRatio={ILLUSTRATION_ASPECT_RATIO}
              alt={ILLUSTRATION_ALT}
              src={image.url}
            />
          )}
        </ImageWrapper>
        <Body>{children}</Body>
        {iconName && (
          <ThemeProvider theme={reversedTheme}>
            <IconWrapper>
              <Icon size={SIZE.LARGE} name={iconName} />
            </IconWrapper>
          </ThemeProvider>
        )}
      </Layout>
    </Wrapper>
  )
}

Card.propTypes = {
  image: PropTypes.object.isRequired,
  url: PropTypes.string,
  children: PropTypes.node.isRequired,
  iconName: PropTypes.string,
  dataCy: PropTypes.string,
}

Card.defaultProps = {
  iconName: null,
  dataCy: null,
  url: null,
}

export default Card
